import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { Zap, Mail, Lock, User, Phone, ArrowLeft, Eye, EyeOff, Bike } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerRegister = () => {
  const navigate = useNavigate();
  const { registerCustomer } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    telefono: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await registerCustomer(formData);
      toast.success('Registrazione completata! Ora puoi aggiungere i tuoi veicoli.');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante la registrazione';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Image */}
      <div className="hidden lg:block relative">
        <img 
          src="https://global.niucache.com/static/i18n/widget/component/img/n-gloss-red_8bf830c3.jpg"
          alt="NIU Scooter"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/30"></div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-white max-w-md">
            <Zap className="w-16 h-16 text-primary mb-6" />
            <h1 className="font-heading text-4xl font-black mb-4">Unisciti a Noi</h1>
            <p className="text-white/80 text-lg">
              Registrati per prenotare il tagliando della tua NIU in modo semplice e veloce.
            </p>
            <div className="mt-8 p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <div className="flex items-center gap-3">
                <Bike className="w-8 h-8 text-primary" />
                <div>
                  <p className="font-semibold">Registrazione Semplificata</p>
                  <p className="text-sm text-white/70">Aggiungi i tuoi veicoli dopo la registrazione</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        <div className="w-full max-w-md py-8">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Torna alla home
          </Link>

          <Card className="border-border/50">
            <CardHeader className="space-y-1">
              <div className="flex items-center gap-2 lg:hidden mb-4">
                <Zap className="w-8 h-8 text-primary" />
                <span className="font-heading font-bold text-xl">NIU Service</span>
              </div>
              <CardTitle className="font-heading text-2xl">Registrati</CardTitle>
              <CardDescription>
                Crea il tuo account per prenotare tagliandi. Potrai aggiungere i tuoi veicoli dopo la registrazione.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="nome">Nome *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="nome"
                        placeholder="Mario"
                        className="pl-10 h-12"
                        value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                        required
                        data-testid="register-nome-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cognome">Cognome *</Label>
                    <Input
                      id="cognome"
                      placeholder="Rossi"
                      className="h-12"
                      value={formData.cognome}
                      onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                      required
                      data-testid="register-cognome-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="tu@esempio.com"
                      className="pl-10 h-12"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telefono">Telefono *</Label>
                  <div className="relative flex">
                    <div className="flex items-center justify-center px-3 h-12 bg-muted border border-r-0 border-input rounded-l-md text-sm font-medium text-muted-foreground">
                      +39
                    </div>
                    <Input
                      id="telefono"
                      type="tel"
                      placeholder="333 123 4567"
                      className="h-12 rounded-l-none"
                      value={formData.telefono}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                      required
                      data-testid="register-telefono-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Minimo 6 caratteri"
                      className="pl-10 pr-10 h-12"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={6}
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 font-bold"
                  disabled={isLoading}
                  data-testid="register-submit-btn"
                >
                  {isLoading ? 'Registrazione in corso...' : 'Registrati'}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm">
                <span className="text-muted-foreground">Hai già un account? </span>
                <Link to="/login" className="text-primary font-medium hover:underline" data-testid="go-to-login-link">
                  Accedi
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CustomerRegister;
